import React, { useState } from 'react';
import { useSortableTable } from './hooks/useSortableTable';
import { apiFetch } from './utils/api';

function EmployeeTimesheetTab({ employeeId, timesheet, onRefresh }) {
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Date range filtering
  const [dateRange, setDateRange] = useState('month');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  
  // Filter timesheet by date range
  const getFilteredTimesheet = () => {
    const now = new Date();
    let startDate, endDate;
    
    switch(dateRange) {
      case 'week':
        startDate = new Date(now);
        startDate.setDate(now.getDate() - now.getDay() + 1);
        endDate = new Date(startDate);
        endDate.setDate(startDate.getDate() + 6);
        break;
      case 'month':
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        break;
      case 'year':
        startDate = new Date(now.getFullYear(), 0, 1);
        endDate = new Date(now.getFullYear(), 11, 31);
        break;
      case 'custom':
        if (!customStartDate || !customEndDate) return timesheet;
        startDate = new Date(customStartDate);
        endDate = new Date(customEndDate);
        break;
      default:
        return timesheet;
    }
    
    return timesheet.filter(item => {
      const workDate = new Date(item.work_date);
      return workDate >= startDate && workDate <= endDate;
    });
  };
  
  // Sorting
  const filteredTimesheet = getFilteredTimesheet();
  const { items: sortedTimesheet, requestSort, getSortIndicator } = useSortableTable(filteredTimesheet, { key: 'work_date', direction: 'desc' });
  const [formData, setFormData] = useState({
    work_date: new Date().toISOString().split('T')[0],
    clock_in: '09:00',
    clock_out: '17:00',
    break_minutes: 60,
    hours_worked: 7.0,
    overtime_hours: 0,
    notes: ''
  });

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  const formatTime = (timeString) => {
    if (!timeString) return '-';
    // Handle both full datetime and time-only strings
    if (timeString.includes('T')) {
      return new Date(timeString).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    }
    return timeString;
  };

  const calculateHours = (clockIn, clockOut, breakMinutes) => {
    if (!clockIn || !clockOut) return 0;
    
    const [inHour, inMin] = clockIn.split(':').map(Number);
    const [outHour, outMin] = clockOut.split(':').map(Number);
    
    const inMinutes = (inHour * 60) + inMin;
    const outMinutes = (outHour * 60) + outMin;
    
    let totalMinutes = outMinutes - inMinutes;
    if (totalMinutes < 0) totalMinutes += 24 * 60; // Handle overnight shifts
    
    totalMinutes -= (breakMinutes || 0);
    
    return Math.max(0, totalMinutes / 60);
  };

  const handleAdd = () => {
    setEditingItem(null);
    setFormData({
      work_date: new Date().toISOString().split('T')[0],
      clock_in: '09:00',
      clock_out: '17:00',
      break_minutes: 60,
      hours_worked: 7.0,
      overtime_hours: 0,
      notes: ''
    });
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      work_date: item.work_date ? item.work_date.split('T')[0] : '',
      clock_in: formatTime(item.clock_in),
      clock_out: formatTime(item.clock_out),
      break_minutes: item.break_minutes || 0,
      hours_worked: item.hours_worked || 0,
      overtime_hours: item.overtime_hours || 0,
      notes: item.notes || ''
    });
    setShowModal(true);
  };

  const handleTimeChange = () => {
    const calculated = calculateHours(formData.clock_in, formData.clock_out, formData.break_minutes);
    setFormData({
      ...formData,
      hours_worked: parseFloat(calculated.toFixed(2))
    });
  };

  const handleSave = async () => {
    if (!formData.work_date) {
      alert('Work date is required');
      return;
    }

    setSaving(true);
    try {
      const url = editingItem
        ? `/api/employees/timesheet/${editingItem.id}`
        : '/api/employees/timesheet';

      const response = await apiFetch(url, {
        method: editingItem ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          employee_id: employeeId
        })
      });

      if (response.ok) {
        setShowModal(false);
        onRefresh();
      } else {
        alert('Error saving timesheet entry');
      }
    } catch (error) {
      console.error('Error saving timesheet:', error);
      alert('Error saving timesheet entry');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, workDate) => {
    if (!window.confirm(`Delete timesheet for ${formatDate(workDate)}?`)) return;

    try {
      await apiFetch(`/api/employees/timesheet/${id}`, {
        method: 'DELETE'
      });
      onRefresh();
    } catch (error) {
      console.error('Error deleting timesheet:', error);
      alert('Error deleting timesheet entry');
    }
  };

  const totalHours = timesheet.reduce((sum, item) => sum + (parseFloat(item.hours_worked) || 0), 0);
  const totalOvertime = timesheet.reduce((sum, item) => sum + (parseFloat(item.overtime_hours) || 0), 0);

  return (
    <>
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Time & Attendance</h2>
            {timesheet.length > 0 && (
              <p className="text-sm text-gray-600 mt-1">
                Total: {totalHours.toFixed(2)}h regular, {totalOvertime.toFixed(2)}h overtime
              </p>
            )}
          </div>
          <button 
            onClick={handleAdd}
            className="bg-gray-300 hover:bg-gray-400 text-black px-4 py-2 rounded transition-colors"
          >
            + Add Entry
          </button>
        </div>

        {/* Date Range Filter */}
        <div className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <div className="flex items-center gap-4">
            <label className="text-sm font-medium text-gray-700">View:</label>
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="px-3 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-slate-500"
            >
              <option value="week">This Week</option>
              <option value="month">This Month</option>
              <option value="year">This Year</option>
              <option value="custom">Custom Range</option>
            </select>
            
            {dateRange === 'custom' && (
              <>
                <input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  className="px-3 py-1.5 border border-gray-300 rounded text-sm"
                  placeholder="Start date"
                />
                <span className="text-gray-500">to</span>
                <input
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  className="px-3 py-1.5 border border-gray-300 rounded text-sm"
                  placeholder="End date"
                />
              </>
            )}
            
            <div className="ml-auto text-sm text-gray-600">
              Showing <strong>{sortedTimesheet.length}</strong> of <strong>{timesheet.length}</strong> entries
            </div>
          </div>
        </div>

      {sortedTimesheet.length === 0 ? (
        <p className="text-gray-500 text-center py-8">No timesheet entries</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-300">
              <tr>
                <th 
                  className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase cursor-pointer hover:bg-gray-400 transition-colors select-none"
                  onClick={() => requestSort('work_date')}
                >
                  Date{getSortIndicator('work_date')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Clock In</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Clock Out</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Break (min)</th>
                <th 
                  className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase cursor-pointer hover:bg-gray-400 transition-colors select-none"
                  onClick={() => requestSort('hours_worked')}
                >
                  Hours{getSortIndicator('hours_worked')}
                </th>
                <th 
                  className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase cursor-pointer hover:bg-gray-400 transition-colors select-none"
                  onClick={() => requestSort('overtime_hours')}
                >
                  Overtime{getSortIndicator('overtime_hours')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sortedTimesheet.map((item, idx) => (
                <tr key={item.id} className={idx % 2 === 1 ? 'bg-slate-50' : 'bg-white'}>
                  <td className="px-4 py-3 text-sm text-gray-900 font-medium">{formatDate(item.work_date)}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{formatTime(item.clock_in)}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{formatTime(item.clock_out)}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{item.break_minutes || 0}</td>
                  <td className="px-4 py-3 text-sm text-gray-900 font-medium">{parseFloat(item.hours_worked || 0).toFixed(2)}h</td>
                  <td className="px-4 py-3 text-sm text-orange-600 font-medium">
                    {item.overtime_hours ? `${parseFloat(item.overtime_hours).toFixed(2)}h` : '-'}
                  </td>
                  <td className="px-4 py-3 text-sm space-x-2">
                    <button
                      onClick={() => handleEdit(item)}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(item.id, item.work_date)}
                      className="text-red-600 hover:text-red-800"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>

    {/* Add/Edit Modal */}
    {showModal && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
          <div className="sticky top-0 bg-slate-600 text-white px-6 py-4 rounded-t-lg">
            <h3 className="text-xl font-semibold">
              {editingItem ? 'Edit Timesheet Entry' : 'Add Timesheet Entry'}
            </h3>
          </div>

          <div className="p-6 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Work Date *</label>
                <input
                  type="date"
                  value={formData.work_date}
                  onChange={(e) => setFormData({...formData, work_date: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-slate-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Break (minutes)</label>
                <input
                  type="number"
                  value={formData.break_minutes}
                  onChange={(e) => setFormData({...formData, break_minutes: parseInt(e.target.value) || 0})}
                  onBlur={handleTimeChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-slate-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Clock In</label>
                <input
                  type="time"
                  value={formData.clock_in}
                  onChange={(e) => setFormData({...formData, clock_in: e.target.value})}
                  onBlur={handleTimeChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-slate-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Clock Out</label>
                <input
                  type="time"
                  value={formData.clock_out}
                  onChange={(e) => setFormData({...formData, clock_out: e.target.value})}
                  onBlur={handleTimeChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-slate-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Hours Worked</label>
                <input
                  type="number"
                  step="0.25"
                  value={formData.hours_worked}
                  onChange={(e) => setFormData({...formData, hours_worked: parseFloat(e.target.value) || 0})}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-slate-500"
                />
                <p className="text-xs text-gray-500 mt-1">Auto-calculated from clock times</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Overtime Hours</label>
                <input
                  type="number"
                  step="0.25"
                  value={formData.overtime_hours}
                  onChange={(e) => setFormData({...formData, overtime_hours: parseFloat(e.target.value) || 0})}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-slate-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                rows={3}
                placeholder="Site worked, tasks completed, etc."
                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-slate-500"
              />
            </div>
          </div>

          <div className="sticky bottom-0 bg-gray-50 px-6 py-4 rounded-b-lg flex justify-end space-x-3">
            <button
              onClick={() => setShowModal(false)}
              className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-100"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-black rounded disabled:opacity-50"
            >
              {saving ? 'Saving...' : (editingItem ? 'Update' : 'Add Entry')}
            </button>
          </div>
        </div>
      </div>
    )}
  </>
  );
}

export default EmployeeTimesheetTab;
